import React, { useState, useCallback } from "react";
import { Button, Card, Col, Row, Spin, Tag, Typography, Alert, Image, Tooltip } from "antd";
import { WiDaySunny } from "react-icons/wi";
import { BsCloudRainHeavy, BsGlobe2, BsCloudDrizzle, BsDropletHalf } from "react-icons/bs";
import { FiRefreshCw, FiDownload } from "react-icons/fi";
import { TbWorldLatitude, TbDropletOff } from "react-icons/tb";
import apiRb from "../../http/apiRb";

const { Title, Text } = Typography;

const GRUPOS = [
  { indice: 0, nome: "Previsões de Precipitação Brasil",   descricao: "GFS Semana 1 e 2 — Total e Anomalia", icon: <BsCloudRainHeavy size={22} />, cor: "#1677ff", tag: "Brasil" },
  { indice: 1, nome: "Precipitações e Anomalias Brasil",   descricao: "Chuva acumulada 7 e 30 dias",          icon: <BsCloudDrizzle  size={22} />, cor: "#0ea5e9", tag: "Brasil" },
  { indice: 2, nome: "Previsões de Precipitação Argentina",descricao: "GFS Semana 1 e 2 — Total e Anomalia", icon: <BsCloudRainHeavy size={22} />, cor: "#7c3aed", tag: "Argentina" },
  { indice: 3, nome: "Precipitações e Anomalias Argentina",descricao: "Chuva acumulada 7 e 30 dias",          icon: <BsCloudDrizzle  size={22} />, cor: "#6d28d9", tag: "Argentina" },
  { indice: 4, nome: "América do Sul e EUA",               descricao: "EUA e América do Sul — 8 mapas GFS",  icon: <BsGlobe2        size={22} />, cor: "#059669", tag: "Global" },
  { indice: 5, nome: "Chuva Acumulada EUA",                descricao: "7 e 30 dias — Total e Anomalia",      icon: <TbWorldLatitude size={22} />, cor: "#d97706", tag: "EUA" },
  { indice: 6, nome: "Disponibilidade de Água no Solo - Brasil", descricao: "Mapa diário de disponibilidade hídrica no solo", icon: <BsDropletHalf  size={22} />, cor: "#0891b2", tag: "Agritempo" },
  { indice: 7, nome: "Estiagem Agrícola - Brasil",          descricao: "Mapa diário de estiagem agrícola",       icon: <TbDropletOff    size={22} />, cor: "#dc2626", tag: "Agritempo" },
];

const initEstado = () =>
  Object.fromEntries(GRUPOS.map((g) => [g.indice, { imagens: [], loading: false, erro: null }]));

export default function Clima() {
  const [estado, setEstado] = useState(initEstado);

  const gerarGrupo = useCallback(async (indice) => {
    setEstado((p) => ({ ...p, [indice]: { imagens: [], loading: true, erro: null } }));
    try {
      const { data } = await apiRb.get(`/clima/gerar/${indice}`, { timeout: 360000 });
      const imagens = (data.imagens || []).map((img) => ({
        titulo: img.titulo || "Mapa",
        url: img.b64 ? `data:image/png;base64,${img.b64}` : null,
        erro: img.erro,
      }));
      if (!imagens.length) throw new Error(data.error || "Nenhuma imagem retornada");
      setEstado((p) => ({ ...p, [indice]: { imagens, loading: false, erro: null } }));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Erro desconhecido";
      setEstado((p) => ({ ...p, [indice]: { imagens: [], loading: false, erro: msg } }));
    }
  }, []);

  const regenerarMapa = useCallback(async (indice, idx) => {
    setEstado((p) => {
      const imgs = [...(p[indice]?.imagens || [])];
      imgs[idx] = { ...imgs[idx], loading: true, erro: null };
      return { ...p, [indice]: { ...p[indice], imagens: imgs } };
    });
    try {
      const { data } = await apiRb.get(`/clima/mapa/${indice}/${idx}`, { timeout: 120000 });
      const img = data.imagem || {};
      setEstado((p) => {
        const imgs = [...(p[indice]?.imagens || [])];
        imgs[idx] = {
          titulo: img.titulo || imgs[idx]?.titulo || "Mapa",
          url: img.b64 ? `data:image/png;base64,${img.b64}` : null,
          erro: img.erro,
        };
        return { ...p, [indice]: { ...p[indice], imagens: imgs } };
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Erro";
      setEstado((p) => {
        const imgs = [...(p[indice]?.imagens || [])];
        imgs[idx] = { ...imgs[idx], loading: false, erro: msg };
        return { ...p, [indice]: { ...p[indice], imagens: imgs } };
      });
    }
  }, []);

  function baixar(url, nome) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${nome.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
  }

  function baixarTodas(imagens, nomeGrupo) {
    imagens.forEach((img, i) => img.url && setTimeout(() => baixar(img.url, `${nomeGrupo}_${i + 1}_${img.titulo}`), i * 400));
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <WiDaySunny size={36} color="#f59e0b" />
        <div>
          <Title level={3} style={{ margin: 0 }}>Clima Agrícola</Title>
          <Text type="secondary">Mapas NOAA com textos detectados por IA e traduzidos — dados do mapa preservados</Text>
        </div>
      </div>

      <Alert
        style={{ marginBottom: 24 }}
        type="info"
        showIcon
        message="Cada mapa é processado individualmente: GPT-4o detecta e traduz os textos, o Sharp aplica as edições. Os dados de cores do mapa nunca são alterados. Mapas Agritempo têm marcações cardinais removidas automaticamente."
      />

      <Row gutter={[20, 20]}>
        {GRUPOS.map((grupo) => {
          const { imagens, loading, erro } = estado[grupo.indice];

          return (
            <Col key={grupo.indice} xs={24} sm={24} md={12} lg={12} xl={8}>
              <Card
                style={{ borderTop: `3px solid ${grupo.cor}`, borderRadius: 12, height: "100%" }}
                styles={{ body: { padding: 16 } }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: grupo.cor }}>{grupo.icon}</span>
                    <Text strong style={{ fontSize: 15 }}>{grupo.nome}</Text>
                  </div>
                  <Tag color={grupo.cor}>{grupo.tag}</Tag>
                </div>

                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 14 }}>
                  {grupo.descricao}
                </Text>

                {loading && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 180, background: "#f9fafb", borderRadius: 8, gap: 10 }}>
                    <Spin size="large" />
                    <Text type="secondary" style={{ fontSize: 12 }}>Processando mapas via GPT-4o + Sharp…</Text>
                  </div>
                )}

                {!loading && erro && (
                  <Alert type="error" showIcon message="Erro" description={erro} style={{ marginBottom: 12, fontSize: 12 }} />
                )}

                {!loading && imagens.length > 0 && (
                  <Image.PreviewGroup>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {imagens.map((img, idx) => (
                        <div key={idx}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                            <Text strong style={{ fontSize: 12, color: grupo.cor }}>{img.titulo}</Text>
                            {img.url && (
                              <Tooltip title="Regenerar este mapa">
                                <Button
                                  size="small"
                                  icon={<FiRefreshCw size={11} />}
                                  loading={img.loading}
                                  onClick={() => regenerarMapa(grupo.indice, idx)}
                                  style={{ fontSize: 11 }}
                                >
                                  Regenerar
                                </Button>
                              </Tooltip>
                            )}
                          </div>

                          {img.url ? (
                            <div style={{ position: "relative" }}>
                              <Image
                                src={img.url}
                                alt={img.titulo}
                                style={{ width: "100%", height: "auto", objectFit: "contain", maxHeight: 720, borderRadius: 8, border: "1px solid #e5e7eb", display: "block" }}
                              />
                              <Button
                                size="small"
                                icon={<FiDownload />}
                                onClick={() => baixar(img.url, img.titulo)}
                                style={{ position: "absolute", top: 8, right: 8, opacity: 0.85, zIndex: 1 }}
                              >
                                Baixar
                              </Button>
                            </div>
                          ) : (
                            <Alert type="warning" showIcon message={`Falha: ${img.erro || "Erro desconhecido"}`} style={{ fontSize: 11 }} />
                          )}
                        </div>
                      ))}
                    </div>
                  </Image.PreviewGroup>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <Button
                    type="primary"
                    icon={<FiRefreshCw />}
                    loading={loading}
                    onClick={() => gerarGrupo(grupo.indice)}
                    style={{ flex: 1, background: grupo.cor, borderColor: grupo.cor }}
                  >
                    {imagens.length > 0 ? "Regenerar Todos" : "Gerar Mapas"}
                  </Button>
                  {imagens.length > 0 && imagens.some((i) => i.url) && (
                    <Button icon={<FiDownload />} onClick={() => baixarTodas(imagens, grupo.nome)}>
                      Baixar Todos
                    </Button>
                  )}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
